import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar,
  faBullseye,
  faPen,
  faPuzzlePiece,
  faArrowLeft,
  faBolt,
  faGlobe,
} from '@fortawesome/free-solid-svg-icons';
import Modal from './Modal';
import CustomQuestion from './CustomQuestion';
import Alert from '../components/Alert';
import './styles/GameGallery.css';

interface GameTypeCard {
  id: string;
  route?: string;
  module?: string;
  icon: typeof faStar;
  badgeIcon: typeof faBolt;
  title: string;
  subtitle: string;
  description: string;
  bgColor: string;
  comingSoon?: boolean;
}

const gameTypes: GameTypeCard[] = [
  {
    id: 'ai',
    route: '/dashboard/games/smart',
    icon: faStar,
    badgeIcon: faBolt,
    title: 'Smart Quiz',
    subtitle: 'AI-powered quiz generation',
    description: 'Enter a topic and let AI build the quiz for you',
    bgColor: 'var(--color-dark)',
  },
  {
    id: 'geo',
    route: '/dashboard/games/geo',
    icon: faBullseye,
    badgeIcon: faGlobe,
    title: 'Geo Guess',
    subtitle: 'Location-based challenges',
    description: 'Drop a pin on the map, test geography knowledge',
    bgColor: 'var(--color-primary)',
  },
  {
    id: 'custom',
    module: 'custommodule',
    icon: faPen,
    badgeIcon: faPen,
    title: 'Custom Quiz',
    subtitle: 'Create your own questions',
    description: 'Multiple choice, true/false, short answer',
    bgColor: 'var(--color-card-green)',
    comingSoon: true,
  },
  {
    id: 'sketcher',
    route: '/dashboard/games/sketcher',
    icon: faPuzzlePiece,
    badgeIcon: faPuzzlePiece,
    title: 'Sketcher',
    subtitle: 'Drawing & guessing game',
    description: 'Sketch a prompt and let players guess what it is',
    bgColor: 'var(--color-card-brown)',
  },
];

const GameGallery: React.FC = () => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [module, setModule] = useState('');
  const [message, setMessage] = useState('');
  const [isInfoAlertOpen, setIsInfoAlertOpen] = useState(false);
  const [gameCode, setGameCode] = useState('');
  const navigate = useNavigate();

  const handleCardClick = (g: GameTypeCard) => {
    if (g.route) {
      navigate(g.route);
    } else if (g.module) {
      setModule(g.module);
      setModalOpen(true);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setModule('');
  };

  const handleOnResponseMessage = (feedback: {
    severity: string;
    content: string;
  }) => {
    setMessage(feedback.content);
    setIsInfoAlertOpen(true);
  };

  const handleGameCreationSuccess = () => {
    closeModal();
    navigate('/dashboard/home');
  };

  const handleJoinGame = () => {
    if (!gameCode.trim()) return;
    navigate(`/join?code=${gameCode.trim()}`);
  };

  return (
    <div className="mygames">
      <div className="mygames__header">
        <button
          className="mygames__back"
          onClick={() => navigate('/dashboard/home')}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <div>
          <h1 className="mygames__title">What kind of game?</h1>
          <p className="mygames__subtitle">Choose a game type to get started</p>
        </div>
      </div>

      <p className="mygames__section-label">Game Type</p>

      <div className="mygames__grid">
        {gameTypes.map((g) => (
          <button
            key={g.id}
            className={`mygames__card${g.comingSoon ? ' mygames__card--disabled' : ''}`}
            style={{ backgroundColor: g.bgColor }}
            onClick={g.comingSoon ? undefined : () => handleCardClick(g)}
            disabled={g.comingSoon}
          >
            {g.comingSoon && <span className="mygames__card-coming-soon">Coming Soon</span>}
            <div className="mygames__card-top">
              <FontAwesomeIcon icon={g.icon} className="mygames__card-icon" />
              <FontAwesomeIcon
                icon={g.badgeIcon}
                className="mygames__card-badge"
              />
            </div>
            <div className="mygames__card-body">
              <h3 className="mygames__card-title">{g.title}</h3>
              <p className="mygames__card-sub">{g.subtitle}</p>
              <p className="mygames__card-desc">{g.description}</p>
            </div>
          </button>
        ))}
      </div>

      <div className="mygames__join">
        <h3 className="mygames__join-title">Join a Game</h3>
        <p className="mygames__join-sub">Have a game code? Enter it below</p>
        <div className="mygames__join-row">
          <input
            className="mygames__join-input"
            type="text"
            placeholder="Enter game code (e.g. QV8K3M)"
            value={gameCode}
            onChange={(e) => setGameCode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleJoinGame()}
          />
          <button className="mygames__join-btn" onClick={handleJoinGame}>
            Join
          </button>
        </div>
      </div>

      {/* Modals for non-route game types */}
      {isModalOpen && module === 'custommodule' && (
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          resetMessage={handleOnResponseMessage}
        >
          <CustomQuestion />
        </Modal>
      )}

      {isInfoAlertOpen && (
        <Alert
          title="Message"
          isOpen={isInfoAlertOpen}
          cancelText="Ok"
          message={message}
          onCancel={() => setIsInfoAlertOpen(false)}
        />
      )}
    </div>
  );
};

export default GameGallery;
